import { PALETTE_PRESETS } from '../lib/palettes'

/** The name of the palette that is on, for the line beside the label. */
function chosenLabel(look: { palette: string[] }): string {
  const p = PALETTE_PRESETS.find(
    (x) =>
      x.colors.length === look.palette.length &&
      x.colors.every((c, i) => c.toLowerCase() === look.palette[i]?.toLowerCase()),
  )
  return p?.label ?? 'Custom'
}
import { Slider } from './bits'

/**
 * HOW A RUN LOOKS, IN ONE PLACE.
 *
 * The same three questions get asked twice in this app and they have to have
 * the same answer both times: what color, how close together, how big. Before
 * you draw, they set what the NEXT run will be; after you draw, they change
 * the run you are looking at. Two implementations of that would drift, and the
 * drift would show up as the swatch picked before drawing not matching the
 * swatch shown afterward on the same run.
 *
 * So this component knows nothing about which of the two it is doing. It is
 * handed values and a patch function, and the caller decides whether that
 * patch lands on a Strand or on the defaults.
 *
 * No inches on the spacing label. There is no scale step in V1, so an inch
 * figure would be a guess wearing the costume of a measurement.
 */

/** The colors actually installed, in the order they get asked for. */
const COLOR_IDS = ['warm', 'pure', 'red', 'green', 'blue', 'multi']

export interface Look {
  palette: string[]
  patternRepeat: number
  spacingScale: number
  bulbScale: number
  /** How thick the thing being wrapped is. Trunks only. */
  wrapWidth: number
  /** A collar of a different color at the top of a trunk. */
  wrapCap: boolean
  capPalette: string[]
  /** How far down the trunk the collar comes, as a fraction of it. */
  capLength: number
}

/*
 * OPEN ONCE, STAYS OPEN FOR THE SESSION.
 *
 * Module scope rather than component state on purpose: the panel is unmounted
 * and remounted every time the selection changes, so component state would
 * snap the disclosure shut between two runs a person is comparing. Somebody
 * who has just opened the adjustments is about to open them again.
 */
let moreOpen = false

/*
 * AND THE SIX COLOR TILES ARE CLOSED UNTIL SOMEBODY WANTS THEM.
 *
 * The tiles are good and they stay exactly as they are. What was wrong is
 * that all six were on screen at rest, which is a hundred and eighty pixels
 * of chooser sitting there to tell you one fact: the light is warm white.
 * At rest that fact is one line. Press Change and the six tiles are back.
 *
 * Module scope for the same reason as moreOpen: this panel is unmounted and
 * remounted on every selection change, so component state would slam the
 * tiles shut between two runs a person is comparing.
 */
let colorOpen = false

export default function LookControls({
  look,
  onChange,
  /** Spacing and size are about a line of bulbs. A wreath has neither. */
  spacing = true,
  /**
   * HOW THICK THE TRUNK IS.
   *
   * Only where something is being wrapped. A palm at the end of a driveway and
   * one by the door are different trunks and take different amounts of light,
   * and until now the only way to say so was to place the run and then find a
   * Size slider on the selected one. Asked for as: for palm trees we can
   * adjust the thickness just like the bulb spacing, because some palm trees
   * are thicker or thinner.
   */
  thickness = false,
  /**
   * THE CAP: A COLLAR OF A DIFFERENT COLOR AT THE TOP OF A TRUNK.
   *
   * Warm white up the palm and the top foot or two in red is how these are
   * actually sold, and it was not buildable here at all. Trunks only.
   */
  cap = false,
  /**
   * THE PRODUCT SITS ON THE SAME LINE AS THE COLOR.
   *
   * The reference frames read Warm white, C9 - one row, one sentence, one
   * Change. Color and product are two halves of one answer to what is going
   * on this roof, and splitting them across two controls made a person set
   * one and then go looking for the other.
   */
  product,
  /** What the disclosure is called. Adjust on a selected run, per the frames. */
  moreLabel = 'More adjustments',
  /**
   * WHAT GOES BETWEEN THE COLOR AND THE ADJUSTMENTS.
   *
   * The reference frames put Snap to roof edge directly under the color row,
   * because it is the other thing a person sets before drawing. It cannot be
   * rendered after this component without landing under the disclosure, and
   * it does not belong inside it: it changes how a click behaves, not how a
   * run looks, and everything in there is how a run looks.
   */
  beforeMore,
}: {
  look: Look
  onChange: (patch: Partial<Look>) => void
  spacing?: boolean
  thickness?: boolean
  cap?: boolean
  product?: string
  moreLabel?: string
  beforeMore?: React.ReactNode
}) {
  return (
    <>
      {/*
        THE SWATCH IS THE INFORMATION.

        This was six gray boxes in a two column grid, each holding a small
        color pill and the word for it, which is a form control standing in
        front of the only thing a person is actually choosing: the color. On
        a product about how a house will look, the color should be the size
        of the target and nothing should be in front of it.

        A row of swatches, and the name of the chosen one printed underneath -
        so the color is never the only thing carrying the choice, which is the
        rule that mattered here and still does.
      */}
      {/*
        THE COLOUR TILES, TO THE SPEC.

        I had turned these into bare circles and argued the swatch is the
        information. The prototype had already answered it better: a two
        column grid of named tiles, a 14px dot over the word, and the chosen
        one filled pale green with the label in bold and a check in the
        corner. The name is on every tile, so color is never carrying the
        choice on its own - which is the rule I was trying to serve by
        printing one name above the row, and the file's way of serving it is
        better because it holds for all six at once.

        Read out of a running copy of the prototype rather than off its
        stylesheet: the file contains an older .swatch rule as well, and the
        thing it actually renders is .color-preset.
      */}
      <details
        className="color-more"
        open={colorOpen}
        onToggle={(e) => {
          colorOpen = (e.currentTarget as HTMLDetailsElement).open
        }}
      >
        <summary>
          <span className="color-now">
            <span
              className={
                look.palette.length > 1 ? 'color-dot color-dot-multi' : 'color-dot'
              }
              aria-hidden="true"
              style={{ background: look.palette[0] }}
            >
              {look.palette.length > 1 &&
                look.palette.map((c, i) => <i key={i} style={{ background: c }} />)}
            </span>
            <span className="color-name">
              {product ? `${chosenLabel(look)}, ${product}` : chosenLabel(look)}
            </span>
          </span>
          <span className="color-change">Change</span>
        </summary>
      <div className="color-presets" role="group" aria-label="Light color">
        {COLOR_IDS.map((id) => {
          const p = PALETTE_PRESETS.find((x) => x.id === id)
          if (!p) return null
          const on =
            p.colors.length === look.palette.length &&
            p.colors.every((c, i) => c.toLowerCase() === look.palette[i]?.toLowerCase())
          return (
            <button
              key={id}
              className="color-preset"
              aria-pressed={on}
              aria-label={p.label}
              onClick={() => onChange({ palette: [...p.colors], patternRepeat: p.patternRepeat })}
            >
              <span
                className={p.colors.length > 1 ? 'color-dot color-dot-multi' : 'color-dot'}
                aria-hidden="true"
                style={{ background: p.colors[0] }}
              >
                {p.colors.length > 1 &&
                  p.colors.map((c, i) => <i key={i} style={{ background: c }} />)}
              </span>
              <span className="preset-label">{p.label}</span>
              <span className="preset-check" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m5 12.5 4.5 4.5L19 7.5" />
                </svg>
              </span>
            </button>
          )
        })}
        </div>
      </details>

      {beforeMore}

      {/*
        MORE ADJUSTMENTS, COLLAPSED, as the design package has it.

        Spacing, thickness, the accent and bulb size were all open at once, so
        the panel was a column of six sliders and the color - the thing a
        person changes twenty times an hour - was pushed to the top of a long
        scroll. The file puts every one of them behind a disclosure and shows
        the color alone. Open it once and it stays open for the session,
        because somebody adjusting spacing is about to adjust it again.
      */}
      <details
        className="appearance-more"
        open={moreOpen}
        onToggle={(e) => {
          moreOpen = (e.currentTarget as HTMLDetailsElement).open
        }}
      >
        <summary>{moreLabel}</summary>
      {spacing && (
        <>
          {/*
            THE HEADING NAMES IT; THE ENDS SAY WHICH WAY IS WHICH.
            
            Each slider used to carry a sentence of its own - How close
            together, How big the bulbs look - directly under a heading that
            said the same thing in one word. And then a readout: 4 of 10, which
            is a measurement of nothing. There is no unit behind it and nobody
            has an opinion about whether four is right.
            
            What a person wants to know is which direction does what. So the
            heading names the control and two words under the track say where
            each end goes.
          */}
          <p className="insp-lbl plain">Spacing</p>
          <Slider
            label=""
            value={look.spacingScale}
            min={0.35}
            max={3}
            step={0.05}
            onChange={(v) => onChange({ spacingScale: v })}
            ends={['Tighter', 'Looser']}
          />
          {thickness && (
            <>
              <p className="insp-lbl plain">Trunk thickness</p>
              <Slider
                label=""
                value={look.wrapWidth}
                min={0.006}
                max={0.14}
                step={0.002}
                onChange={(v) => onChange({ wrapWidth: v })}
                ends={['Thinner', 'Thicker']}
              />
            </>
          )}
          {cap && (
            <>
              <label className="switch-row">
                <input
                  type="checkbox"
                  checked={look.wrapCap}
                  onChange={(e) => onChange({ wrapCap: e.target.checked })}
                />
                {/*
                  IT WAS CALLED "CAP THE TOP" AND NOBODY KNEW WHAT A CAP WAS.
                  It is a second color at the top of the trunk, which is how
                  these are actually sold, so it is named for that. Same
                  property, same stored values: only the words changed.
                */}
                <span>Top accent</span>
              </label>
              {!look.wrapCap && <p className="insp-note">A second color at the top.</p>}
              {look.wrapCap && (
                <>
                  {/*
                    A ROW, NOT A SECOND GRID.

                    The cap needs the same six colors the trunk does, and
                    repeating the grid made the panel twice as tall for a
                    choice that is made once and rarely changed. Asked for as:
                    any way to make this side more simple, maybe hide the
                    colors for cap and have a clean dropdown.

                    A native select, so it is one line, opens over everything,
                    and behaves like a picker on a tablet without any of this
                    file knowing how a tablet works.
                  */}
                  <div className="cap-row">
                    <label htmlFor="cap-color">Accent color</label>
                    <span
                      className="cap-swatch"
                      style={{ background: look.capPalette[0] ?? '#E02B2B' }}
                      aria-hidden="true"
                    />
                    <select
                      id="cap-color"
                      value={
                        COLOR_IDS.find((id) => {
                          const c = PALETTE_PRESETS.find((x) => x.id === id)
                          return (
                            c &&
                            c.colors.length === look.capPalette.length &&
                            c.colors.every(
                              (v, i) => v.toLowerCase() === look.capPalette[i]?.toLowerCase(),
                            )
                          )
                        }) ?? 'red'
                      }
                      onChange={(e) => {
                        const c = PALETTE_PRESETS.find((x) => x.id === e.target.value)
                        if (c) onChange({ capPalette: [...c.colors] })
                      }}
                    >
                      {COLOR_IDS.map((id) => {
                        const c = PALETTE_PRESETS.find((x) => x.id === id)
                        return c ? (
                          <option key={id} value={id}>
                            {c.label}
                          </option>
                        ) : null
                      })}
                    </select>
                  </div>
                  <p className="insp-lbl plain">Accent height</p>
                  <Slider
                    label=""
                    value={look.capLength}
                    min={0.04}
                    max={0.5}
                    step={0.01}
                    onChange={(v) => onChange({ capLength: v })}
                    ends={['Shorter', 'Taller']}
                  />
                </>
              )}
            </>
          )}
          <p className="insp-lbl plain">Bulb size</p>
          <Slider
            label=""
            value={look.bulbScale}
            min={0.4}
            max={2.6}
            step={0.05}
            onChange={(v) => onChange({ bulbScale: v })}
            ends={['Smaller', 'Larger']}
          />
        </>
      )}
      </details>
    </>
  )
}
